import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faUser, faCheck, faChevronDown, faArrowLeft, faPhone, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import LogoutModal from "../Modals/logout";
import SelectFamilySideModal from "../Modals/SelectFamilySideModal";
import { getApi } from "../Services/apiService";
import { canViewPhoneNumberReport } from "../Utils/phoneNumberReportAccess";
import {
  syncHeadOfLineRoleFromApi,
  getFamilyTreeIdFromStorage,
  setFamilyTreeIdInLoggedInRole,
  clearFamilyTreeIdsFromStorage,
} from "../Utils/headOfLineRole";
import {
  isAdminWithoutActiveSubscription,
  isLoggedInUserTreeAdminFromStorage,
  isSubscriptionSettingsLocation,
  OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT,
  OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT,
} from "../Utils/subscriptionAccess";
import { formatTopOfLineFamilyTitle, getUnspacedTopOfLineFamilyTitle } from "../Utils/topOfLineFamilyTitle";
import { setAppBadge, requestBadgePermission } from "../Utils/appBadge";
import {
  HEADER_PREFERRED_FAMILY_SIDE_KEY,
  SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY,
  FAMILY_SIDE_MODAL_SESSION_USER_ID_KEY,
} from "../Utils/importTreeSideLock";

/** Admin header family-side dropdown: show short labels while keeping API `family_side` strings in state/storage. */
function formatFamilySideHeaderLabel(side) {
  if (side == null || String(side).trim() === "") return "";
  const norm = String(side)
    .trim()
    .toLowerCase()
    .replace(/'/g, "")
    .replace(/\s+/g, " ");
  if (norm === "mothers side" || norm === "mother side") return "Mother";
  if (norm === "fathers side" || norm === "father side") return "Father";
  return String(side).trim();
}

function getFamilySideHeaderColorModifier(side) {
  if (side == null || String(side).trim() === "") return "";
  const norm = String(side)
    .trim()
    .toLowerCase()
    .replace(/'/g, "")
    .replace(/\s+/g, " ");
  if (norm === "mothers side" || norm === "mother side" || norm === "mother") {
    return "mother";
  }
  if (norm === "fathers side" || norm === "father side" || norm === "father") {
    return "father";
  }
  return "";
}

/** Member pick-side modal: parentheses copy uses apostrophes (API still uses `Mother Side` / `Father Side`). */
function formatFamilySideModalParenLabel(side) {
  if (side == null || String(side).trim() === "") return "";
  const norm = String(side)
    .trim()
    .toLowerCase()
    .replace(/'/g, "")
    .replace(/\s+/g, " ");
  if (norm === "mothers side" || norm === "mother side") return "Mother's Side";
  if (norm === "fathers side" || norm === "father side") return "Father's Side";
  return String(side).trim();
}

/* Role dropdown options — restore when showing Role filter again
const TREE_ROLE_OPTIONS = [
  { value: "0", label: "Admin" },
  { value: "1", label: "User" },
];
*/

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const subscriptionModalsAllowed = () =>
    !isSubscriptionSettingsLocation(locationRef.current);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const [user, setUser] = useState({});
  const [roleLabel, setRoleLabel] = useState("User"); // "Admin" | "User" from loggedIn_user_role.is_admin

  // Admin display for user role: name and color line from userDetial/admin_details
  const [adminDisplay, setAdminDisplay] = useState({ name: "", colorLine: "", hexCode: "" });

  /** Header family line from top_of_the_line — Georgia Pro Black 16px, letter-spaced (formatTopOfLineFamilyTitle) */
  const [topOfLineFamilyTitle, setTopOfLineFamilyTitle] = useState("");
  /** Same title unspaced (e.g. "Witherspoon & Patterson Family") — used in the select-family-side copy. */
  const [plainFamilyTitle, setPlainFamilyTitle] = useState("");

  // Notification dropdown state
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const notificationDropdownRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  /** Filter for GET /my-family-trees: 0 = trees I created (Admin), 1 = member trees (User) — kept in sync with head-of-line role when storage updates */
  const [treeRoleType, setTreeRoleType] = useState(() =>
    isLoggedInUserTreeAdminFromStorage() ? "0" : "1"
  );
  const [familySideOptions, setFamilySideOptions] = useState([]);
  const [selectedFamilySide, setSelectedFamilySide] = useState("");
  const [myFamilyTreesList, setMyFamilyTreesList] = useState([]);
  const [myFamilyTreesLoading, setMyFamilyTreesLoading] = useState(false);

  const [isFamilySideCustomOpen, setIsFamilySideCustomOpen] = useState(false);
  const familySideDropdownRef = useRef(null);
  /** When role filter changes, always sync storage from the new list (Admin vs User trees differ). */
  const prevTreeRoleTypeRef = useRef(null);

  /** Member-only: choose Mother/Father after login (replaces header dropdown for User role). */
  const [showSelectFamilyModal, setShowSelectFamilyModal] = useState(false);

  // Get user from local storage
  // useEffect(() => {
  //   const loadUser = () => {
  //     try {
  //       const storedUser = JSON.parse(localStorage.getItem("userDetail"));
  //       if (storedUser) {
  //         setUser(storedUser);
  //       }
  //     } catch (err) {
  //       console.error("Error parsing userDetail from localStorage:", err);
  //       setUser({});
  //     }
  //   };

  //   // Load once on mount
  //   loadUser();

  //   // Listen for localStorage changes
  //   window.addEventListener("storage", loadUser);
  //   window.addEventListener("userUpdated", loadUser);

  //   // Cleanup
  //   return () => {
  //     window.removeEventListener("storage", loadUser);
  //     window.removeEventListener("userUpdated", loadUser);
  //   };
  // }, []);
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
        const treeId = getFamilyTreeIdFromStorage();
        const mergedUser = {
          ...storedUser,
          ...(treeId
            ? { familyTreeId: treeId, family_tree_id: treeId }
            : {}),
        };
        if (mergedUser && Object.keys(mergedUser).length > 0) {
          setUser(prevUser => {
            // Only update if user data actually changed to prevent infinite loops
            const prevString = JSON.stringify(prevUser);
            const storedString = JSON.stringify(mergedUser);
            if (prevString !== storedString) {
              return mergedUser;
            }
            return prevUser;
          });
        }
      } catch (err) {
        console.error("Error parsing userDetail from localStorage:", err);
        setUser({});
      }
    };

    // Initial update on mount
    loadUser();

    // Listen for storage changes (when localStorage is updated from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === "userDetail" || e.key === null) {
        loadUser();
      }
    };

    // Listen for custom storage events (for same-tab updates)
    const handleCustomStorageChange = () => {
      loadUser();
    };

    // Check localStorage when window regains focus (handles same-tab updates)
    const handleFocus = () => {
      loadUser();
    };

    window.addEventListener("storage", handleStorageChange);
    // Custom event for same-tab localStorage updates
    window.addEventListener("localStorageChange", handleCustomStorageChange);
    window.addEventListener("userUpdated", handleCustomStorageChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
      window.removeEventListener("userUpdated", handleCustomStorageChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []); // Empty dependency array - only set up listeners once

  // Profile label and Role dropdown both follow head-of-line storage; manual Role changes clear storage first, then refetch repopulates (see clearHeadOfLineRoleLocalStorageForTreeFilterChange).
  useEffect(() => {
    const updateRoleFromStorage = () => {
      try {
        const isAdmin = isLoggedInUserTreeAdminFromStorage();
        setRoleLabel(isAdmin ? "Admin" : "User");
        setTreeRoleType(isAdmin ? "0" : "1");
      } catch {
        setRoleLabel("User");
        setTreeRoleType("1");
      }
    };
    updateRoleFromStorage();
    const onHeadOfLineRoleUpdated = () => {
      updateRoleFromStorage();
    };
    window.addEventListener("loggedInUserRoleUpdated", onHeadOfLineRoleUpdated);
    window.addEventListener("focus", updateRoleFromStorage);
    return () => {
      window.removeEventListener("loggedInUserRoleUpdated", onHeadOfLineRoleUpdated);
      window.removeEventListener("focus", updateRoleFromStorage);
    };
  }, []);

  /** New account in this tab: allow "Select family" modal again when user id changes. */
  useEffect(() => {
    try {
      const raw = localStorage.getItem("userDetail");
      const uid = raw ? JSON.parse(raw)?.id : null;
      if (uid == null) return;
      const prev = sessionStorage.getItem(FAMILY_SIDE_MODAL_SESSION_USER_ID_KEY);
      if (prev != null && String(prev) !== String(uid)) {
        sessionStorage.removeItem(SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY);
      }
      sessionStorage.setItem(FAMILY_SIDE_MODAL_SESSION_USER_ID_KEY, String(uid));
    } catch (_) {
      /* ignore */
    }
  }, [user?.id]);

  /** Persist tree id on `loggedIn_user_role` (and top-level key); sync React user state from userDetail + tree id. */
  const persistFamilyTreeId = useCallback((treeId) => {
    if (!treeId) return;
    const id = String(treeId);
    try {
      if (
        String(getFamilyTreeIdFromStorage() || "") === id &&
        localStorage.getItem("familyTreeId") === id
      ) {
        return;
      }
      setFamilyTreeIdInLoggedInRole(id);
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      setUser({ ...localUser, familyTreeId: id, family_tree_id: id });
    } catch (e) {
      console.error("persistFamilyTreeId:", e);
    }
  }, []);

  useEffect(() => {
    const fetchMyFamilyTrees = async () => {
      const openAdminSubscriptionModalIfNeeded = () => {
        if (
          subscriptionModalsAllowed() &&
          treeRoleType === "0" &&
          isAdminWithoutActiveSubscription()
        ) {
          window.dispatchEvent(new Event(OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT));
        }
      };

      setMyFamilyTreesLoading(true);
      try {
        const response = await getApi(`/my-family-trees?type=${treeRoleType}`);
        if (response?.statusCode === 200 && Array.isArray(response?.data)) {
          const data = response.data;
          setMyFamilyTreesList(data);
          const uniqueSides = [
            ...new Set(
              data
                .map((item) => item?.family_side)
                .filter((s) => s != null && String(s).trim() !== "")
            ),
          ];
          setFamilySideOptions(uniqueSides);

          const shouldDeferUserLineSubscriptionForSelectFamilyModal = () => {
            if (treeRoleType !== "1") return false;
            if (!data || data.length === 0) return false;
            try {
              return (
                sessionStorage.getItem(SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY) !==
                "1"
              );
            } catch {
              return true;
            }
          };

          if (data.length === 0) {
            setSelectedFamilySide("");
            try {
              localStorage.removeItem(HEADER_PREFERRED_FAMILY_SIDE_KEY);
            } catch (_) {
              /* ignore */
            }
            prevTreeRoleTypeRef.current = treeRoleType;
            await syncHeadOfLineRoleFromApi();
            if (
              treeRoleType === "1" &&
              subscriptionModalsAllowed() &&
              !shouldDeferUserLineSubscriptionForSelectFamilyModal()
            ) {
              try {
                const r = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
                if (r.is_admin === false && r.is_subscription === false) {
                  window.dispatchEvent(new Event(OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT));
                }
              } catch (_) {
                /* ignore */
              }
            }
            openAdminSubscriptionModalIfNeeded();
            return;
          }

          const roleJustChanged =
            prevTreeRoleTypeRef.current !== null &&
            prevTreeRoleTypeRef.current !== treeRoleType;
          prevTreeRoleTypeRef.current = treeRoleType;

          /** After role switch, user should land on Dashboard with data for the new tree id. */
          let shouldNavigateToDashboardAfterRoleChange = false;

          const applyFirstTree = () => {
            const first = data[0];
            const side = first?.family_side;
            setSelectedFamilySide(
              side != null && String(side).trim() !== "" ? side : ""
            );
            if (first?.id) {
              persistFamilyTreeId(first.id);
            }
            try {
              if (side != null && String(side).trim() !== "") {
                localStorage.setItem(HEADER_PREFERRED_FAMILY_SIDE_KEY, String(side).trim());
              }
            } catch (_) {
              /* ignore */
            }
          };

          let preferredRaw = "";
          try {
            preferredRaw = localStorage.getItem(HEADER_PREFERRED_FAMILY_SIDE_KEY) || "";
          } catch (_) {
            preferredRaw = "";
          }
          const preferred =
            preferredRaw != null && String(preferredRaw).trim() !== ""
              ? String(preferredRaw).trim()
              : "";

          if (roleJustChanged) {
            applyFirstTree();
            shouldNavigateToDashboardAfterRoleChange = true;
          } else if (preferred && uniqueSides.includes(preferred)) {
            const row = data.find((t) => t.family_side === preferred);
            const side = row?.family_side ?? preferred;
            setSelectedFamilySide(
              side != null && String(side).trim() !== "" ? String(side).trim() : ""
            );
            if (row?.id) {
              persistFamilyTreeId(row.id);
            } else {
              clearFamilyTreeIdsFromStorage();
            }
          } else {
            const localId = getFamilyTreeIdFromStorage();
            const matchById = localId
              ? data.find((t) => String(t.id) === String(localId))
              : null;

            if (matchById) {
              const side = matchById.family_side;
              setSelectedFamilySide(
                side != null && String(side).trim() !== "" ? side : ""
              );
              if (matchById.id) {
                persistFamilyTreeId(matchById.id);
              }
              try {
                if (side != null && String(side).trim() !== "") {
                  localStorage.setItem(HEADER_PREFERRED_FAMILY_SIDE_KEY, String(side).trim());
                }
              } catch (_) {
                /* ignore */
              }
            } else {
              applyFirstTree();
            }
          }

          const treeIdForSync = getFamilyTreeIdFromStorage();
          let preferredForOmit = "";
          try {
            preferredForOmit = (
              localStorage.getItem(HEADER_PREFERRED_FAMILY_SIDE_KEY) || ""
            ).trim();
          } catch (_) {
            preferredForOmit = "";
          }
          const preferredRowForSync =
            preferredForOmit &&
            data.find((t) => t.family_side === preferredForOmit);
          const omitUnscopedTreeIdsFromRole =
            !treeIdForSync &&
            preferredRowForSync &&
            !preferredRowForSync.id;

          await syncHeadOfLineRoleFromApi(treeIdForSync || undefined, {
            omitTreeIdsFromResponseWhenUnscoped: omitUnscopedTreeIdsFromRole,
          });
          if (
            treeRoleType === "1" &&
            subscriptionModalsAllowed() &&
            !shouldDeferUserLineSubscriptionForSelectFamilyModal()
          ) {
            try {
              const r = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
              if (r.is_admin === false && r.is_subscription === false) {
                window.dispatchEvent(new Event(OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT));
              }
            } catch (_) {
              /* ignore */
            }
          }
          openAdminSubscriptionModalIfNeeded();
          if (shouldNavigateToDashboardAfterRoleChange) {
            navigate("/dashboard");
          }
        } else {
          setMyFamilyTreesList([]);
          setFamilySideOptions([]);
          setSelectedFamilySide("");
        }
      } catch (err) {
        console.error("Error fetching my-family-trees:", err);
        setMyFamilyTreesList([]);
        setFamilySideOptions([]);
        setSelectedFamilySide("");
      } finally {
        setMyFamilyTreesLoading(false);
      }
    };
    fetchMyFamilyTrees();
  }, [treeRoleType, persistFamilyTreeId, navigate]);

  const openLogoutModal = () => {
    setIsLogoutModalOpen(true);
    setIsDropdownOpen(false);
  };
  const closeLogoutModal = () => setIsLogoutModalOpen(false);

  // Format timestamp to "X mins ago" format
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return ''
    
    // Convert string timestamp to number if needed
    const timestampNum = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp
    
    const now = new Date()
    const notificationDate = new Date(timestampNum)
    
    // Check if date is valid
    if (isNaN(notificationDate.getTime())) return ''
    
    const diffInMs = now - notificationDate
    const diffInMins = Math.floor(diffInMs / 60000)
    const diffInHours = Math.floor(diffInMs / 3600000)
    const diffInDays = Math.floor(diffInMs / 86400000)
    const diffInWeeks = Math.floor(diffInDays / 7)
    const diffInMonths = Math.floor(diffInDays / 30)
    const diffInYears = Math.floor(diffInDays / 365)

    if (diffInMins < 1) return 'Just now'
    if (diffInMins < 60) return `${diffInMins} ${diffInMins === 1 ? 'min' : 'mins'} ago`
    if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`
    if (diffInDays < 7) return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`
    if (diffInWeeks < 4) return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`
    if (diffInMonths < 12) return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`
  }

  // Fetch notification count (total is top-level on response, not inside data)
  const fetchNotificationCount = async () => {
    try {
      const params = new URLSearchParams({
        filter: 'all',
        limit: '1000',
        offset: '0'
      })

      const response = await getApi(`/notifications?${params.toString()}`)
      const totalFromApi = response?.total

      if (totalFromApi !== undefined && totalFromApi !== null) {
        const count = Number(totalFromApi)
        setNotificationCount(Number.isNaN(count) ? 0 : count)
      } else {
        const notificationsData = response?.data
        if (Array.isArray(notificationsData)) {
          setNotificationCount(notificationsData.length)
        } else if (Array.isArray(notificationsData?.data)) {
          setNotificationCount(notificationsData.data.length)
        } else {
          setNotificationCount(0)
        }
      }
    } catch (err) {
      console.error('Error fetching notification count:', err)
      setNotificationCount(0)
    }
  }

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true)
      
      const params = new URLSearchParams({
        filter: 'all',
        limit: '5', // Only fetch top 5
        offset: '0'
      })

      const response = await getApi(`/notifications?${params.toString()}`)
      const notificationsData = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data

      if (notificationsData && Array.isArray(notificationsData) && notificationsData.length > 0) {
        const mappedNotifications = notificationsData.map((notification, index) => {
          const hasProfileImage = notification.profile_image && 
                                 typeof notification.profile_image === 'string' && 
                                 notification.profile_image.trim() !== ''
          const profileImage = hasProfileImage 
            ? notification.profile_image 
            : window.getPublicUrl('/assets/images/dummy-profile.jpg')

          return {
            id: notification.id,
            profileImage,
            title: notification.title || 'Notification',
            body: notification.message || '',
            timestamp: formatTimeAgo(notification.createdAt),
            // is_read: 0 = unread (false), is_read: 1 = read (true)
            // Also check for isRead (future API format)
            isRead: notification.isRead !== undefined 
              ? notification.isRead === true 
              : (notification.is_read === 1)
          }
        })
        
        setNotifications(mappedNotifications)
      } else {
        setNotifications([])
      }
      
      // Also update the count when fetching notifications
      fetchNotificationCount()
    } catch (err) {
      console.error('Error fetching notifications:', err)
      setNotifications([])
    } finally {
      setNotificationsLoading(false)
    }
  }

  const toggleNotificationDropdown = () => {
    const newState = !isNotificationDropdownOpen
    setIsNotificationDropdownOpen(newState)
    
    // Fetch notifications when opening dropdown (always fetch fresh data)
    // fetchNotifications already calls fetchNotificationCount, so no need to call it separately
    if (newState) {
      fetchNotifications()
    }
  }

  // Poll notification count every 10 seconds
  useEffect(() => {
    // Fetch count immediately on mount
    fetchNotificationCount()

    // Set up interval to poll every 10 seconds
    const intervalId = setInterval(() => {
      fetchNotificationCount()
    }, 10000) // 10000ms = 10 seconds

    // Coming back to the app should show the real count straight away rather
    // than up to a poll interval later - it is the moment the icon badge and
    // the bell are compared side by side.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchNotificationCount()
    }
    document.addEventListener('visibilitychange', onVisible)

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, []) // Empty dependency array - only run on mount/unmount

  // Mirror the bell badge onto the installed app's home-screen icon
  useEffect(() => {
    setAppBadge(notificationCount)
  }, [notificationCount])

  const handleImageError = (e) => {
    if (e.target.src !== window.getPublicUrl('/assets/images/dummy-profile.jpg')) {
      e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg')
    }
  }

  // Derive admin name and color line from localStorage
  // Stored under loggedIn_user_role: { family_tree_id, admin_details: { name, color_name, hex_code } }
  const updateAdminDisplay = useCallback(() => {
    const familyTreeId =
      getFamilyTreeIdFromStorage() ||
      user?.family_tree_id ||
      user?.familyTreeId;
    if (!familyTreeId) {
      setAdminDisplay({ name: "", colorLine: "", hexCode: "" });
      return;
    }
    let name = "";
    let colorLine = "";
    let hexCode = "";

    const readAdminFromStorage = (raw) => {
      if (!raw || typeof raw !== "object") return null;
      const fid = raw.family_tree_id ?? raw.familyTreeId;
      const treeMatches =
        fid == null || fid === "" || String(fid) === String(familyTreeId);
      // Keyed by family_tree_id: storage[family_tree_id].admin_details
      const byTree = raw[familyTreeId];
      if (byTree?.admin_details && typeof byTree.admin_details === "object") {
        return byTree.admin_details;
      }
      // Root admin_details only when missing tree id (legacy) or it matches current tree — never another tree
      if (raw.admin_details && typeof raw.admin_details === "object" && treeMatches) {
        return raw.admin_details;
      }
      return null;
    };

    const applyAdminDetails = (adminDetails) => {
      if (!adminDetails) return false;
      name = adminDetails.name ?? "";
      colorLine = adminDetails.color_name ?? adminDetails.hex_code ?? "";
      hexCode = adminDetails.hex_code ?? "";
      return !!(name || colorLine);
    };

    // Primary: loggedIn_user_role (where app stores family_tree_id + admin_details per screenshot)
    try {
      const loggedInRole = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
      if (applyAdminDetails(readAdminFromStorage(loggedInRole))) {
        setAdminDisplay({ name, colorLine, hexCode });
        return;
      }
    } catch {
      // ignore
    }
    try {
      const userDetial = JSON.parse(localStorage.getItem("userDetial") || "{}");
      if (applyAdminDetails(readAdminFromStorage(userDetial))) {
        setAdminDisplay({ name, colorLine, hexCode });
        return;
      }
    } catch {
      // ignore
    }
    try {
      const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
      if (applyAdminDetails(readAdminFromStorage(userDetail))) {
        setAdminDisplay({ name, colorLine, hexCode });
        return;
      }
    } catch {
      // ignore
    }
    try {
      const standalone = JSON.parse(localStorage.getItem("admin_details") || "null");
      if (standalone && typeof standalone === "object" && applyAdminDetails(standalone)) {
        setAdminDisplay({ name, colorLine, hexCode });
        return;
      }
    } catch {
      // ignore
    }
    setAdminDisplay({ name, colorLine, hexCode });
  }, [user?.family_tree_id, user?.familyTreeId]);

  useEffect(() => {
    updateAdminDisplay();
    window.addEventListener("localStorageChange", updateAdminDisplay);
    window.addEventListener("focus", updateAdminDisplay);
    window.addEventListener("loggedInUserRoleUpdated", updateAdminDisplay);
    return () => {
      window.removeEventListener("localStorageChange", updateAdminDisplay);
      window.removeEventListener("focus", updateAdminDisplay);
      window.removeEventListener("loggedInUserRoleUpdated", updateAdminDisplay);
    };
  }, [updateAdminDisplay]);

  useEffect(() => {
    const syncFamilyTitle = () => {
      try {
        const role = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
        const useCompact =
          typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
        setTopOfLineFamilyTitle(
          useCompact
            ? getUnspacedTopOfLineFamilyTitle(role.top_of_the_line)
            : formatTopOfLineFamilyTitle(role.top_of_the_line)
        );
        setPlainFamilyTitle(getUnspacedTopOfLineFamilyTitle(role.top_of_the_line));
      } catch {
        setTopOfLineFamilyTitle("");
        setPlainFamilyTitle("");
      }
    };
    syncFamilyTitle();
    const mq = window.matchMedia("(max-width: 768px)");
    mq.addEventListener("change", syncFamilyTitle);
    window.addEventListener("localStorageChange", syncFamilyTitle);
    window.addEventListener("focus", syncFamilyTitle);
    window.addEventListener("loggedInUserRoleUpdated", syncFamilyTitle);
    return () => {
      mq.removeEventListener("change", syncFamilyTitle);
      window.removeEventListener("localStorageChange", syncFamilyTitle);
      window.removeEventListener("focus", syncFamilyTitle);
      window.removeEventListener("loggedInUserRoleUpdated", syncFamilyTitle);
    };
  }, []);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        profileRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !profileRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target)
      ) {
        setIsNotificationDropdownOpen(false);
      }
      if (
        familySideDropdownRef.current &&
        !familySideDropdownRef.current.contains(event.target)
      ) {
        setIsFamilySideCustomOpen(false);
      }
    };

    // if (isDropdownOpen) {
    //   document.addEventListener("mousedown", handleClickOutside);
    // }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleFamilySideCustomDropdown = () => {
    if (myFamilyTreesLoading || familySideOptions.length === 0) return;
    setIsFamilySideCustomOpen((o) => !o);
  };

  const familySideTriggerLabel = myFamilyTreesLoading
    ? "Loading…"
    : familySideOptions.length === 0
      ? "No options"
      : selectedFamilySide
        ? formatFamilySideHeaderLabel(selectedFamilySide)
        : "Select side";
  const selectedFamilySideColorModifier =
    getFamilySideHeaderColorModifier(selectedFamilySide);

  const applyFamilySideSelection = useCallback(
    async (selection, markFamilyModalDone = false) => {
      let tree = null;
      let side = "";
      if (
        selection != null &&
        typeof selection === "object" &&
        selection.treeId != null &&
        String(selection.treeId).trim() !== ""
      ) {
        tree = myFamilyTreesList.find(
          (t) => String(t.id) === String(selection.treeId)
        );
        side =
          tree?.family_side != null
            ? String(tree.family_side).trim()
            : String(selection.familySide || "").trim();
      } else {
        side = String(selection || "").trim();
        tree = myFamilyTreesList.find((t) => t.family_side === side);
      }
      try {
        if (side) {
          localStorage.setItem(HEADER_PREFERRED_FAMILY_SIDE_KEY, side);
        }
      } catch (_) {
        /* ignore */
      }
      if (tree?.id) {
        persistFamilyTreeId(tree.id);
        await syncHeadOfLineRoleFromApi(tree.id);
      } else {
        clearFamilyTreeIdsFromStorage();
        await syncHeadOfLineRoleFromApi(undefined, {
          omitTreeIdsFromResponseWhenUnscoped: true,
        });
      }
      if (treeRoleType === "1" && subscriptionModalsAllowed()) {
        try {
          const r = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
          if (r.is_admin === false && r.is_subscription === false) {
            window.dispatchEvent(new Event(OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT));
          }
        } catch (_) {
          /* ignore */
        }
      }
      navigate("/dashboard");
      setSelectedFamilySide(side);
      setIsFamilySideCustomOpen(false);
      if (markFamilyModalDone) {
        try {
          sessionStorage.setItem(SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY, "1");
        } catch (_) {
          /* ignore */
        }
        setShowSelectFamilyModal(false);
      }
    },
    [myFamilyTreesList, persistFamilyTreeId, navigate, treeRoleType]
  );

  /** One modal row per tree (API can return many rows with the same `family_side`). */
  const selectFamilyModalOptions = useMemo(() => {
    return myFamilyTreesList
      .filter((row) => row?.id != null && String(row.id).trim() !== "")
      .map((row) => {
        const treeId = String(row.id).trim();
        const side =
          row?.family_side != null ? String(row.family_side).trim() : "";
        const creator =
          row?.creator_name != null ? String(row.creator_name).trim() : "";
        const displaySide = formatFamilySideModalParenLabel(side);
        const label = creator
          ? `${creator} (${displaySide || "Family"})`
          : displaySide || "Family";
        return { treeId, familySide: side, label, creatorName: creator };
      });
  }, [myFamilyTreesList]);

  const selectedTreeIdForModal = useMemo(() => {
    try {
      const sid =
        getFamilyTreeIdFromStorage() ||
        user?.family_tree_id ||
        user?.familyTreeId;
      return sid != null ? String(sid) : "";
    } catch {
      return "";
    }
  }, [user?.family_tree_id, user?.familyTreeId, myFamilyTreesList]);

  useEffect(() => {
    if (roleLabel !== "User") {
      setShowSelectFamilyModal(false);
      return;
    }
    if (myFamilyTreesLoading) return;
    if (myFamilyTreesList.length === 0) return;
    try {
      if (sessionStorage.getItem(SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY) === "1") {
        return;
      }
    } catch (_) {
      /* ignore */
    }
    setShowSelectFamilyModal(true);
  }, [roleLabel, myFamilyTreesLoading, myFamilyTreesList]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  // Match longest path first so /settings/profile wins over /settings (if added later)
  const pageTitles = [
    ["/dashboard", "Dashboard"],
    ["/user/dashboard", "Dashboard"],
    ["/family-tree", "Family Tree"],
    // ["/family-news", "Line Gatherings"],
    ["/family-news", "Family News"],
    ["/events", "Planned Events"],
    ["/messages", "Messages"],
    ["/reports", "Reports"],
    ["/reports/phone-numbers", "Contact List"],
    ["/settings/profile", "Profile"],
    ["/settings/notifications", "Notifications"],
    ["/settings/password", "Settings"],
    ["/settings/subscription", "Subscription"],
    ["/subscription", "Subscription"], // plans page (distinct from settings subscription)
    ["/profile", "Profile"],
    ["/faq", "FAQ"],
  ];

  const matchedTitle =
    [...pageTitles].sort((a, b) => b[0].length - a[0].length).find(([path]) => location.pathname.startsWith(path))?.[1] ||
    "Dashboard";

  const currentTitle = matchedTitle;

  return (
    <header className="header">
      {/* Top red line */}
      <div className="header-top-border"></div>

      <div className="header-inner">
        {/* Left Section - Dynamic Title */}
        <div className="header-left">
          <h2 className="head-main">{currentTitle}</h2>
          <Link to="/" className="header-back-to-landing">
            <FontAwesomeIcon icon={faArrowLeft} className="header-back-to-landing-icon" aria-hidden />
            Back to Landing Page
          </Link>
        </div>

        {/* Center — Georgia Pro Black / Georgia, 16px, letter-spaced (formatTopOfLineFamilyTitle) */}
        {topOfLineFamilyTitle ? (
          <div
            className="header-center-family-line"
            title={topOfLineFamilyTitle}
          >
            <strong className="header-center-family-line-banner">
              {topOfLineFamilyTitle}
            </strong>
          </div>
        ) : null}

        {/* Right Section */}
        <div className="header-right">
          {roleLabel === "Admin" && (
          <div className="header-tree-filters" aria-busy={myFamilyTreesLoading}>
            {/* Role filter hidden for now
            <div className="header-tree-filter">
              <span className="header-tree-filter-label" id="header-tree-role-label">
                Role
              </span>
              <div
                className="header-custom-dropdown"
                ref={treeRoleDropdownRef}
              >
                <button
                  type="button"
                  className="header-custom-dropdown-trigger"
                  onClick={toggleTreeRoleDropdown}
                  disabled={myFamilyTreesLoading}
                  aria-expanded={isTreeRoleDropdownOpen}
                  aria-haspopup="listbox"
                  aria-labelledby="header-tree-role-label"
                >
                  <span className="header-custom-dropdown-trigger-text">
                    {treeRoleType === "0" ? "Admin" : "User"}
                  </span>
                  <span className="header-custom-dropdown-trigger-chevron" aria-hidden>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </span>
                </button>
                {isTreeRoleDropdownOpen && (
                  <div
                    className="header-custom-dropdown-panel"
                    role="listbox"
                    aria-labelledby="header-tree-role-label"
                  >
                    {TREE_ROLE_OPTIONS.map((opt) => {
                      const selected = treeRoleType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`header-custom-dropdown-item${selected ? " is-selected" : ""}`}
                          onClick={() => {
                            if (opt.value === treeRoleType) {
                              setIsTreeRoleDropdownOpen(false);
                              return;
                            }
                            clearHeadOfLineRoleLocalStorageForTreeFilterChange();
                            setTreeRoleType(opt.value);
                            setIsTreeRoleDropdownOpen(false);
                            navigate("/dashboard");
                          }}
                        >
                          <span className="header-custom-dropdown-item-inner">
                            <span className="header-custom-dropdown-item-text">
                              {opt.label}
                            </span>
                            <span className="header-custom-dropdown-check" aria-hidden>
                              {selected ? <FontAwesomeIcon icon={faCheck} /> : null}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            */}
            <div className="header-tree-filter">
              <span
                className="header-tree-filter-label"
                id="header-tree-family-side-label"
              >
                Family Side
              </span>
              <div
                className="header-custom-dropdown header-custom-dropdown--family-side"
                ref={familySideDropdownRef}
              >
                <button
                  type="button"
                  className="header-custom-dropdown-trigger"
                  onClick={toggleFamilySideCustomDropdown}
                  disabled={myFamilyTreesLoading || familySideOptions.length === 0}
                  aria-expanded={isFamilySideCustomOpen}
                  aria-haspopup="listbox"
                  aria-labelledby="header-tree-family-side-label"
                >
                  <span
                    className={`header-custom-dropdown-trigger-text${
                      selectedFamilySideColorModifier
                        ? ` header-family-side-text--${selectedFamilySideColorModifier}`
                        : ""
                    }`}
                  >
                    {familySideTriggerLabel}
                  </span>
                  <span className="header-custom-dropdown-trigger-chevron" aria-hidden>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </span>
                </button>
                {isFamilySideCustomOpen && familySideOptions.length > 0 && (
                  <div
                    className="header-custom-dropdown-panel"
                    role="listbox"
                    aria-labelledby="header-tree-family-side-label"
                  >
                    {familySideOptions.map((side) => {
                      const selected = selectedFamilySide === side;
                      const sideColorModifier = getFamilySideHeaderColorModifier(side);
                      return (
                        <button
                          key={side}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`header-custom-dropdown-item${selected ? " is-selected" : ""}`}
                          onClick={() => applyFamilySideSelection(side)}
                        >
                          <span className="header-custom-dropdown-item-inner">
                            <span
                              className={`header-custom-dropdown-item-text${
                                sideColorModifier
                                  ? ` header-family-side-text--${sideColorModifier}`
                                  : ""
                              }`}
                            >
                              {formatFamilySideHeaderLabel(side)}
                            </span>
                            <span className="header-custom-dropdown-check" aria-hidden>
                              {selected ? <FontAwesomeIcon icon={faCheck} /> : null}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
          {/* Admin of this family + Contact Admin (user side only) */}
          <div className="header-admin-contact">
          {roleLabel === "User" && (adminDisplay.name || adminDisplay.colorLine) && (() => {
            const full = (adminDisplay.name || "").trim();
            const parts = full.split(/\s+/).filter(Boolean);
            const firstName = parts[0] || "";
            const initials =
              parts.length > 0
                ? parts.map((p) => p[0]).join("").toUpperCase()
                : "";
            return (
              <span className="header-admin-of-family">
                Admin
                {firstName && (
                  <>
                    {" - "}
                    <strong>{firstName}</strong>
                    {initials && ` ("${initials}")`}
                  </>
                )}
                {adminDisplay.colorLine && (
                  <>
                    {"  "}
                    <span className="header-red-dot" aria-hidden>•</span>
                    {"  "}
                    <span style={adminDisplay.hexCode ? { color: adminDisplay.hexCode } : undefined}>
                      {adminDisplay.colorLine} Line
                    </span>
                  </>
                )}
              </span>
            );
          })()}
          {/* Contact Admin - only for non-admin users */}
          {roleLabel === "User" && (
            <button
              type="button"
              className="contact-admin-btn"
              onClick={() => navigate("/messages?open=admin-support")}
              title="Contact Admin"
            >
              Contact Admin
            </button>
          )}
          {/* Donate - only for non-admin users; opens the subscription page.
              Hover hint uses the sidebar "Upcoming Feature" tooltip pattern
              (see .header-donate-tooltip in App.css) instead of a title attr. */}
          {roleLabel === "User" && (
            <span className="header-donate-wrap">
              <button
                type="button"
                className="header-donate-btn"
                onClick={() => navigate("/settings/subscription")}
                aria-describedby="header-donate-hint"
              >
                Donate
              </button>
              <div className="header-donate-tooltip" id="header-donate-hint" role="tooltip">
                If you would like to make a donation, click here.
              </div>
            </span>
          )}
          </div>

          {/* Refresh - mobile only (hidden on desktop in App.css); the installed
              app has no browser reload control */}
          <button
            type="button"
            className="header-refresh-btn"
            onClick={() => window.location.reload()}
            title="Refresh"
            aria-label="Refresh page"
          >
            <FontAwesomeIcon icon={faRotateRight} className="header-refresh-icon" />
          </button>

          {/* Notification - click navigates to Notification tab */}
          <div className="notification-dropdown-wrapper" ref={notificationDropdownRef}>
            <button
              className="notify-btn"
              onClick={() => {
                // Opening notifications is the natural moment to ask for the
                // permission iOS requires before it will badge the app icon
                requestBadgePermission()
                navigate('/settings/notifications')
              }}
              title="Click to view notifications"
              type="button"
            >
              <FontAwesomeIcon icon={faBell} className="notify-icon" />
              {notificationCount > 0 && (
                <span className="notify-badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
              )}
            </button>

            {/* Notification Dropdown - commented out: navigate directly to notification tab instead */}
            {/* {isNotificationDropdownOpen && (
              <div className="notification-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                {notificationsLoading ? (
                  <div className="notification-dropdown-loading">
                    <p>Loading...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="notification-dropdown-empty">
                    <p>No notification yet</p>
                  </div>
                ) : (
                  <>
                    <div className="notification-dropdown-list">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`notification-dropdown-item notification-dropdown-card ${!notification.isRead ? 'notification-unread' : ''}`}
                        >
                          <div className="notification-dropdown-content">
                            <div className="notification-dropdown-text">
                              <h4 className="notification-dropdown-title">{notification.title}</h4>
                              <p className="notification-dropdown-body">{notification.body}</p>
                              <span className="notification-dropdown-time">{notification.timestamp}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="notification-dropdown-footer">
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault()
                          setIsNotificationDropdownOpen(false)
                          navigate('/settings/notifications')
                        }}
                      >
                        View All
                      </a>
                    </div>
                  </>
                )}
              </div>
            )} */}
          </div>

          {/* Profile */}
          <div className="profile" ref={profileRef} onClick={toggleDropdown}>
            <img
              src={user.profileImage || window.getPublicUrl("/assets/images/dummy-user.svg")}
              alt="Admin"
              className="profile-img"
            />
            <div className="profile-info">
              <span className="profile-name">{user.name}</span>
              <span className="profile-role">{roleLabel}</span>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                className="logout"
                ref={dropdownRef}
                onClick={(e) => e.stopPropagation()}
              >
                <ul>
                  <li>
                    <button 
                      className="logout-btn" 
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate("/settings/profile");
                      }}
                    >
                      <span>
                        <FontAwesomeIcon icon={faUser} />{" "}
                        Profile
                      </span>
                    </button>
                  </li>
                  {canViewPhoneNumberReport() && (
                    <li>
                      <button
                        className="logout-btn"
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate("/reports/phone-numbers");
                        }}
                      >
                        <span>
                          <FontAwesomeIcon icon={faPhone} />{" "}
                          Contact List
                        </span>
                      </button>
                    </li>
                  )}
                  <li>
                    <button className="logout-btn" onClick={openLogoutModal}>
                      <span>
                        <img src={window.getPublicUrl("/assets/images/logout.png")} alt="logout" />{" "}
                        Logout
                      </span>
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <SelectFamilySideModal
        isOpen={showSelectFamilyModal}
        isLoading={myFamilyTreesLoading}
        options={selectFamilyModalOptions}
        selectedTreeId={selectedTreeIdForModal}
        familyName={plainFamilyTitle}
        adminName={adminDisplay.name}
        adminColorLine={adminDisplay.colorLine}
        onSelect={(choice) => applyFamilySideSelection(choice, true)}
      />
      <LogoutModal isOpen={isLogoutModalOpen} onClose={closeLogoutModal} />
    </header>
  );
};

export default Header;
